import { useEffect, useState } from "react";
import { api, errText } from "../lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Switch } from "./ui/switch";
import { Loader2, Stamp, RefreshCw } from "lucide-react";

export default function AdminWatermarkCard() {
  const [status, setStatus] = useState(null);
  const [includeVeo, setIncludeVeo] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/rerender-watermark/status").then((r) => setStatus(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const job = status?.job;
  const running = job && (job.status === "queued" || job.status === "running");

  useEffect(() => {
    if (!running) return;
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [running]);

  const start = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/rerender-watermark", { include_veo: includeVeo });
      if (data.already_running) toast.info("A re-render job is already running");
      else toast.success("Re-render started — posts will resend as each finishes");
      setStatus((s) => ({ ...s, job: data.job }));
      load();
    } catch (e) {
      toast.error(errText(e), { duration: Infinity, closeButton: true });
    } finally {
      setBusy(false);
    }
  };

  const eligible = status?.eligible;
  const target = includeVeo ? (eligible?.total ?? 0) : (eligible?.non_veo ?? 0);

  return (
    <Card data-testid="watermark-fix-card">
      <CardHeader>
        <CardTitle className="font-heading text-xl flex items-center gap-2">
          <Stamp size={18} /> Fix old-watermark posts
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Re-renders every already-sent post that still carries the old top-corner watermark
          (the current engine stamps it bottom-right and never opens on a black frame), then
          re-sends each one. Runs against this environment's database.
        </p>

        <div className="rounded-lg border border-border p-3 text-sm">
          <span className="font-semibold" data-testid="watermark-eligible-count">{eligible?.total ?? "…"}</span> sent posts
          have the old watermark
          {eligible?.veo > 0 && <> — including <span className="font-semibold text-amber-600">{eligible.veo} Veo</span> (slower, uses generation quota)</>}.
        </div>

        <div className="flex items-center justify-between gap-3 rounded-lg border border-border p-3">
          <div>
            <p className="text-sm font-medium">Include Veo posts</p>
            <p className="text-xs text-muted-foreground">Turn off to re-render only the faster non-Veo posts.</p>
          </div>
          <Switch checked={includeVeo} onCheckedChange={setIncludeVeo} disabled={running} data-testid="watermark-include-veo" />
        </div>

        {job && (
          <div className="rounded-lg border border-border p-3 space-y-2" data-testid="watermark-job-status">
            <div className="flex items-center justify-between text-sm">
              <span className="capitalize font-medium flex items-center gap-1.5">
                {running && <Loader2 size={13} className="animate-spin text-accent" />}
                {job.status === "done" ? "Completed" : job.status}
              </span>
              <span className="font-mono text-xs text-muted-foreground">
                {job.done ?? 0}/{job.total ?? 0} done{job.failed ? ` · ${job.failed} failed` : ""}
              </span>
            </div>
            {job.total > 0 && (
              <div className="h-2 rounded-full bg-muted overflow-hidden">
                <div className="h-full rounded-full bg-accent transition-all"
                  style={{ width: `${Math.round(((job.done ?? 0) / job.total) * 100)}%` }} />
              </div>
            )}
            {job.last_post && running && <p className="text-xs text-muted-foreground truncate">Now: {job.last_post}</p>}
          </div>
        )}

        <div className="flex gap-2">
          <Button onClick={start} disabled={busy || running || target === 0} data-testid="watermark-start-btn">
            {busy || running ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Stamp size={14} className="mr-2" />}
            {running ? "Re-rendering…" : `Re-render & resend ${target} post${target === 1 ? "" : "s"}`}
          </Button>
          <Button variant="outline" size="icon" onClick={load} data-testid="watermark-refresh-btn" title="Refresh">
            <RefreshCw size={14} />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
